import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Which lab this account belongs to, according to the account itself.
 *
 * The lab code is also kept in local storage, but that is a per-device
 * convenience and cannot be trusted to answer this: a colleague signing in on a
 * borrowed phone would inherit whatever lab that phone last saw, and their own
 * lab would be invisible on a device they had not used before. The account
 * document is the only answer that travels with the person.
 *
 * A listener rather than a one-off read, because the answer changes while the
 * app is open — joining a lab, or creating one, both write here.
 */
export function useUserLab(uid: string | undefined) {
  const [labCode, setLabCode] = useState<string | null>(null);
  const [labName, setLabName] = useState<string>('');
  const [orgRole, setOrgRole] = useState<'owner' | 'admin' | 'member' | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setLabCode(null);
      setLabName('');
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    return onSnapshot(
      doc(db, 'users', uid),
      (snap) => {
        const data = snap.data();
        setLabCode((data?.labCode as string) || null);
        setLabName((data?.labName as string) || '');
        setOrgRole((data?.orgRole as 'owner' | 'admin' | 'member' | undefined) ?? null);
        setLoading(false);
      },
      () => {
        // A read that fails must not strand anyone on a spinner. Treated as
        // "no lab", which lands on the setup step — recoverable, unlike a
        // blank screen.
        setLabCode(null);
        setLoading(false);
      },
    );
  }, [uid]);

  return { labCode, labName, orgRole, loading };
}
